// TelcoTrack — Úřady: uložená geometrie křížení / dotyku území (WFS ∩ trasa).
// Bez validního GeoJSON se na mapu nic nekreslí — žádné vymyšlené souřadnice.

const GEOJSON_TYPES = new Set([
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
  'GeometryCollection',
]);

export const AUTHORITY_OVERLAY_MAP_EVIDENCE_KEY = 'overlayMap';
/** Layer catalog id when GeoJSON intersection was not stored (live territory overlay). */
export const AUTHORITY_OVERLAY_LAYER_EVIDENCE_KEY = 'overlayLayerId';

export const AUTHORITY_OVERLAY_MAP_MISSING_CS =
  'Geometrii křížení / dotyku s trasou nemáme — na mapě nelze zobrazit. Ověřte trasu VYKOP, dostupnost mapové vrstvy (WFS/DTM) nebo znovu spusťte import z trasy.';

export type AuthorityOverlayMapKind = 'intersection' | 'feature';

export interface AuthorityOverlayMap {
  layerId: string;
  featureId?: string;
  kind: AuthorityOverlayMapKind;
  geometry: Record<string, unknown>;
}

export function isGeoJsonGeometry(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const rec = value as Record<string, unknown>;
  const type = String(rec['type'] ?? '').trim();
  if (!GEOJSON_TYPES.has(type)) return false;
  if (type === 'GeometryCollection') return Array.isArray(rec['geometries']);
  return rec['coordinates'] != null;
}

export function parseAuthorityOverlayMap(payload: unknown): AuthorityOverlayMap | null {
  const rec = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : null;
  if (!rec) return null;
  const raw = rec[AUTHORITY_OVERLAY_MAP_EVIDENCE_KEY];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const map = raw as Record<string, unknown>;
  const layerId = String(map['layerId'] ?? '').trim();
  const kindRaw = map['kind'];
  const kind = kindRaw === 'feature' ? 'feature' : kindRaw === 'intersection' ? 'intersection' : '';
  if (!layerId || !kind || !isGeoJsonGeometry(map['geometry'])) return null;
  const overlay: AuthorityOverlayMap = {
    layerId,
    kind,
    geometry: map['geometry'] as Record<string, unknown>,
  };
  const featureId = String(map['featureId'] ?? '').trim();
  if (featureId) overlay.featureId = featureId;
  return overlay;
}

export function parseAuthorityOverlayLayerId(payload: unknown): string | null {
  const fromMap = parseAuthorityOverlayMap(payload)?.layerId?.trim();
  if (fromMap) return fromMap;
  const rec = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : null;
  if (!rec) return null;
  const id = String(rec[AUTHORITY_OVERLAY_LAYER_EVIDENCE_KEY] ?? '').trim();
  return id || null;
}

export function authorityOverlayLayerIdForEvidence(layerId: string | null | undefined): Record<string, string> | Record<string, never> {
  const id = (layerId ?? '').trim();
  return id ? { [AUTHORITY_OVERLAY_LAYER_EVIDENCE_KEY]: id } : {};
}

export function authorityOverlayMapForEvidence(input: {
  layerId?: string | null;
  featureId?: string | null;
  kind?: AuthorityOverlayMapKind | null;
  geometry?: unknown;
}): Record<string, AuthorityOverlayMap> | Record<string, never> {
  const layerId = (input.layerId ?? '').trim();
  const kind = input.kind === 'feature' || input.kind === 'intersection' ? input.kind : null;
  if (!layerId || !kind || !isGeoJsonGeometry(input.geometry)) return {};
  const overlay: AuthorityOverlayMap = { layerId, kind, geometry: input.geometry };
  const featureId = (input.featureId ?? '').trim();
  if (featureId) overlay.featureId = featureId;
  return { [AUTHORITY_OVERLAY_MAP_EVIDENCE_KEY]: overlay };
}
